import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash, PaperPlaneRight, Image as ImageIcon } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SavedPhotosPanel = ({ onPhotoSelect, onClose, userId }) => {
  const [savedPhotos, setSavedPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { loadSavedPhotos(); }, [userId]);

  const loadSavedPhotos = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API}/saved-photos?user_id=${userId}`);
      setSavedPhotos(res.data.saved_photos || []);
    } catch (e) {
      console.error('Failed to load saved photos:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Only images allowed'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('media_type', 'image');
      const uploadRes = await axios.post(`${API}/upload/media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const saveRes = await axios.post(`${API}/saved-photos`, {
        user_id: userId,
        url: uploadRes.data.url,
        title: file.name
      });

      if (saveRes.data.success) {
        setSavedPhotos([saveRes.data.saved_photo, ...savedPhotos]);
        toast.success('Photo saved!');
      }
    } catch (e) {
      toast.error('Failed to save photo');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (photoId) => {
    try {
      await axios.delete(`${API}/saved-photos/${photoId}?user_id=${userId}`);
      setSavedPhotos(savedPhotos.filter(p => p.id !== photoId));
      toast.success('Photo deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleSend = (photo) => {
    onPhotoSelect({ type: 'image', url: photo.url });
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `${API}${url}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-wa-panel w-full max-w-md rounded-t-2xl max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="saved-photos-panel">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <ImageIcon size={24} className="text-orange-500" />
            Saved Photos
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="p-2 bg-orange-500 text-white rounded-full hover:bg-orange-600 disabled:opacity-50"
              data-testid="add-saved-photo-btn"
            >
              {isUploading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Plus size={20} />}
            </button>
            <button onClick={onClose} className="p-2 hover:bg-wa-hover rounded-full">
              <X size={24} />
            </button>
          </div>
        </div>

        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUploadPhoto} className="hidden" />

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : savedPhotos.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon size={48} className="mx-auto text-gray-300 mb-3" />
              <p className="text-wa-text-secondary">No saved photos</p>
              <p className="text-sm text-wa-text-secondary mt-1">Click + to add photos</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {savedPhotos.map(photo => (
                <div key={photo.id} className="relative group aspect-square rounded-lg overflow-hidden bg-wa-hover">
                  <img
                    src={getImageUrl(photo.url)}
                    alt={photo.title || 'Saved'}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  {/* Delete button - small, top-right corner */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(photo.id); }}
                    className="absolute top-1 right-1 p-1 bg-red-500/80 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-10"
                    title="Delete"
                  >
                    <Trash size={12} />
                  </button>
                  {/* Send button - center, large */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <button
                      onClick={() => handleSend(photo)}
                      className="p-3 bg-wa-primary-strong text-white rounded-full hover:bg-wa-primary-strong shadow-lg"
                      title="Send"
                    >
                      <PaperPlaneRight size={22} weight="fill" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavedPhotosPanel;
